import { guestApiQuery, parseGuestQuery, pseoTypeFromHomeType } from '~/utils/pseo-guest-query'
import { searchFiltersFromQuery, searchFiltersToCountQuery } from '~/utils/search-api-query'
import { formatSejoursParam, type SejourSlug } from '~~/shared/establishment-filters'

/**
 * Aiguillage de la recherche lancée depuis l'accueil (pill du hero et parcours
 * en étapes). Fonction pure : elle ne navigue pas, ne capture rien, ne persiste
 * rien — `useHomeSearch.handleSearch` garde la capture PostHog et la
 * persistance des critères, le parcours n'en fait aucune.
 *
 * - Département (code + slug) → page pSEO `/<code>-<slug>`, type explicite.
 * - Région (slug) → page pSEO `/regions/<slug>`, type explicite (`all` compris :
 *   sans lui ces pages affichent les chambres d'hôtes seules).
 * - Ville, site touristique, texte libre, aucun lieu → `/search`, type ajouté
 *   seulement s'il restreint (vocabulaire API : `cdh` → `chambre-hotes`).
 * - Séjours types : `sejours=a,b` (trié, dédoublonné, `formatSejoursParam`), sur
 *   `/search` comme sur les pages pSEO, qui le lisent aussi.
 *
 * Le chemin renvoyé n'est PAS localisé : l'appelant le passe à `localePath`.
 */

export type HomeSearchType = 'all' | 'cdh' | 'gite'

export interface HomeSearchLocation {
  type: string
  value: string
  code?: string
  slug?: string
  lat?: number
  lng?: number
}

export interface HomeSearchCriteria {
  dates?: { start: string; end: string } | null
  adults: number
  children: number
  /** Âges sortants (déjà filtrés par `useGuestCounters`) ; vide = aucun. */
  childrenAges?: readonly number[]
  /** Séjours types cochés ; vide ou absent = aucun. */
  sejours?: readonly SejourSlug[]
}

export interface HomeSearchRoute {
  /** Chemin non localisé. */
  path: string
  query: Record<string, string>
  /**
   * Partie « lieu » de la query `/search` (persistée par la pill). `null` pour
   * les pages pSEO département/région, que la pill ne persiste pas.
   */
  locationQuery: Record<string, string> | null
}

function withCriteria(locationQuery: Record<string, string>, criteria: HomeSearchCriteria): Record<string, string> {
  const query = { ...locationQuery }
  if (criteria.dates) {
    query.checkIn = criteria.dates.start
    query.checkOut = criteria.dates.end
  }
  if (criteria.adults > 0) query.minAdults = String(criteria.adults)
  if (criteria.children > 0) {
    query.minChildren = String(criteria.children)
    const ages = criteria.childrenAges ?? []
    if (ages.length > 0) query.childrenAges = ages.join(',')
  }
  const sejours = formatSejoursParam(criteria.sejours)
  if (sejours) query.sejours = sejours
  return query
}

/** Type de l'accueil → `type` de `/search`, seulement s'il restreint. */
function withSearchType(query: Record<string, string>, homeType: HomeSearchType): Record<string, string> {
  return homeType === 'all' ? query : { ...query, type: pseoTypeFromHomeType(homeType) }
}

export function resolveHomeSearchRoute(
  location: HomeSearchLocation | null,
  text: string,
  homeType: HomeSearchType,
  criteria: HomeSearchCriteria,
): HomeSearchRoute {
  if (location) {
    if (location.type === 'department' && location.code && location.slug) {
      return {
        path: `/${location.code}-${location.slug}`,
        query: { ...withCriteria({ locationName: location.value }, criteria), type: pseoTypeFromHomeType(homeType) },
        locationQuery: null,
      }
    }

    if (location.type === 'region' && location.slug) {
      return {
        path: `/regions/${location.slug}`,
        query: { ...withCriteria({ locationName: location.value }, criteria), type: pseoTypeFromHomeType(homeType) },
        locationQuery: null,
      }
    }

    // Ville, site touristique, texte libre → page recherche classique
    const locationQuery: Record<string, string> = {}
    if (location.type === 'city') locationQuery.city = location.value
    else if (location.type === 'region') locationQuery.region = location.value
    else if (location.type === 'department' && location.code) {
      locationQuery.department = location.code
      locationQuery.departmentName = location.value
    }
    else if (location.type === 'tourist_site' && location.lat && location.lng) {
      locationQuery.centerLat = String(location.lat)
      locationQuery.centerLng = String(location.lng)
      // Label du POI pour le bandeau « Triés du plus proche au plus éloigné de [POI] »
      locationQuery.locationName = location.value
    }
    else if (location.value) locationQuery.q = location.value
    return {
      path: '/search',
      query: withSearchType(withCriteria(locationQuery, criteria), homeType),
      locationQuery,
    }
  }

  const locationQuery: Record<string, string> = {}
  const trimmed = text.trim()
  if (trimmed) locationQuery.q = trimmed
  return {
    path: '/search',
    query: withSearchType(withCriteria(locationQuery, criteria), homeType),
    locationQuery,
  }
}

/** Code département dans un chemin pSEO `/<code>-<slug>` (même motif que `[codeSlug]/index.vue`). */
const DEPARTMENT_PATH = /^\/(\d{1,3}[A-Za-z]?)-(.+)$/
const REGION_PATH = /^\/regions\/[^/]+$/

/**
 * Query de `/api/search/count` pour la recherche que `resolveHomeSearchRoute`
 * lancerait avec les mêmes arguments : le chiffre annoncé par le parcours est
 * celui de la page ouverte (`meta.total`).
 *
 * Dérivée de la ROUTE produite, pas de l'état du parcours :
 * - page région : `region` (nom) + `type` pSEO explicite + voyageurs et dates
 *   tels que `regions/[slug]/index.vue` les envoie (`parseGuestQuery` +
 *   `guestApiQuery`, pas d'`adults`/`children`, dates seulement si les deux) ;
 * - page département : idem avec `department` = code lu dans le chemin ;
 * - pages pSEO : `sejours` tel que ces pages l'envoient (`formatSejoursParam`) ;
 * - `/search` : la même lecture d'URL que `useSearch` puis la même conversion
 *   que la liste (`searchFiltersToCountQuery`).
 */
export function homeSearchCountQuery(
  location: HomeSearchLocation | null,
  text: string,
  homeType: HomeSearchType,
  criteria: HomeSearchCriteria,
): Record<string, string | number | boolean> {
  const { path, query } = resolveHomeSearchRoute(location, text, homeType, criteria)
  if (path === '/search') return searchFiltersToCountQuery(searchFiltersFromQuery(query))

  const count: Record<string, string | number | boolean> = {}
  if (REGION_PATH.test(path)) {
    count.region = query.locationName ?? ''
  }
  else {
    const match = path.match(DEPARTMENT_PATH)
    count.department = match ? match[1]!.toUpperCase() : ''
  }
  if (query.type) count.type = query.type
  Object.assign(count, guestApiQuery(parseGuestQuery(query)))
  if (query.checkIn && query.checkOut) {
    count.checkIn = query.checkIn
    count.checkOut = query.checkOut
  }
  if (query.sejours) count.sejours = query.sejours
  return count
}
